/* src/ui/scrim.ts — the dimmed backdrop behind a modal (TASKS.md 4.4).
 *
 * One scrim, reference-counted. Two overlapping modals must not stack two backdrops — the second
 * would double the dimming and, worse, closing the first would tear the backdrop out from under
 * the second. So callers take a handle and release it, and the element exists exactly while at
 * least one handle is outstanding.
 *
 * Clicking the scrim is a dismissal request, not a dismissal: it emits, and the modal on top
 * decides. A destructive confirmation should not be cancellable by a stray click behind it.
 */

import { el } from './dom.ts';

let node: HTMLElement | null = null;
let holders = 0;
let onDismiss: (() => void) | null = null;

export interface ScrimHandle {
  release: () => void;
}

/**
 * Show the scrim. `dismiss` is called when the backdrop itself is clicked — the topmost holder
 * wins, which is the one that most recently asked.
 */
export function showScrim(root: HTMLElement, dismiss?: () => void): ScrimHandle {
  holders += 1;
  onDismiss = dismiss ?? null;

  if (!node) {
    node = el('div', {
      class: 'gf-scrim',
      on: {
        pointerdown: (e) => {
          // Only a click *on* the scrim, not one bubbling from the dialog above it.
          if (e.target === node) onDismiss?.();
        },
      },
    });
    root.appendChild(node);
  }

  let released = false;
  return {
    release(): void {
      if (released) return;
      released = true;
      holders -= 1;
      if (holders <= 0) {
        holders = 0;
        node?.remove();
        node = null;
        onDismiss = null;
      }
    },
  };
}
